import type { Clip } from '../features/clips/domain/models/clip.ts'
import { gainDbToLinear } from '../shared/track-gain.ts'
import { applyFades } from './mix-tracks.ts'
import useTracksStore, { trackSelector } from '../shared/store/tracks.store.tsx'
import useMediaStore from '../shared/store/media.store.tsx'
import useAudioStore from '../shared/store/audio.store.tsx'
import { trackAudioformDownload } from './track-audioform-download.ts'

export const downloadClip = async (
  clip: Clip,
  trackEvent?: (event: string, properties?: Record<string, unknown>) => void
): Promise<void> => {
  if (trackEvent) {
    trackAudioformDownload(trackEvent, { assetType: 'clip', format: 'wav' })
  }

  const audioBuffer = useMediaStore.getState().media[clip.mediaId]
  if (!audioBuffer) return

  const track = trackSelector(clip.trackId)(useTracksStore.getState())
  const audioContext = useAudioStore.getState().audioContext
  const clipBuffer = await getAudioBuffer(
    clip,
    audioBuffer,
    track.volume,
    audioContext
  )
  if (!clipBuffer) return
  void downloadAudioBuffer(clipBuffer)
}

const downloadAudioBuffer = async (
  audioBuffer: AudioBuffer,
  fileName = 'audio.wav'
): Promise<void> => {
  const targetSampleRate = 44100
  const processedBuffer = await resampleAudioBuffer(
    audioBuffer,
    targetSampleRate
  )

  const wavBlob = audioBufferToWav(processedBuffer)
  const url = URL.createObjectURL(wavBlob)

  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()

  URL.revokeObjectURL(url)
}

const resampleAudioBuffer = async (
  audioBuffer: AudioBuffer,
  targetSampleRate: number
): Promise<AudioBuffer> => {
  if (audioBuffer.sampleRate === targetSampleRate) return audioBuffer

  const offlineCtx = new OfflineAudioContext(
    audioBuffer.numberOfChannels,
    Math.floor(
      audioBuffer.length * (targetSampleRate / audioBuffer.sampleRate)
    ),
    targetSampleRate
  )

  const source = offlineCtx.createBufferSource()
  source.buffer = audioBuffer
  source.connect(offlineCtx.destination)
  source.start()

  return await offlineCtx.startRendering()
}

const audioBufferToWav = (audioBuffer: AudioBuffer): Blob => {
  const numChannels = audioBuffer.numberOfChannels
  const sampleRate = audioBuffer.sampleRate
  const length = audioBuffer.length
  const numSamples = length * numChannels

  const buffer = new ArrayBuffer(44 + numSamples * 2)
  const view = new DataView(buffer)

  const writeString = (offset: number, str: string): void => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i))
    }
  }

  writeString(0, 'RIFF')
  view.setUint32(4, 36 + numSamples * 2, true) // Size of the file
  writeString(8, 'WAVE')
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true) // Length of the chunk
  view.setUint16(20, 1, true) // PCM Format
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * numChannels * 2, true) // Byte rate
  view.setUint16(32, numChannels * 2, true) // Block size
  view.setUint16(34, 16, true) // Bits per sample
  writeString(36, 'data')
  view.setUint32(40, numSamples * 2, true) // Size of the data

  // Write the PCM samples
  let offset = 44
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numChannels; channel++) {
      let sample = audioBuffer.getChannelData(channel)[i]
      if (!sample) continue
      sample = Math.max(-1, Math.min(1, sample)) // Clamp between -1 and 1
      sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff
      view.setInt16(offset, sample, true)
      offset += 2
    }
  }

  return new Blob([buffer], { type: 'audio/wav' })
}

const getAudioBuffer = async (
  clip: Clip,
  audioBuffer: AudioBuffer,
  baseVolume: number,
  audioContext: AudioContext
): Promise<AudioBuffer | undefined> => {
  if (clip.startAt === undefined) return

  const sampleRate = audioContext.sampleRate
  const duration = audioBuffer.duration - clip.startOffset + clip.endOffset

  const offlineContext = new OfflineAudioContext(
    2,
    duration * sampleRate,
    sampleRate
  )

  const masterGain = offlineContext.createGain()
  masterGain.connect(offlineContext.destination)

  const trackGain = offlineContext.createGain()
  trackGain.gain.value = gainDbToLinear(baseVolume)
  trackGain.connect(masterGain)

  const source = offlineContext.createBufferSource()
  source.buffer = audioBuffer

  const clipGain = offlineContext.createGain()
  source.connect(clipGain)
  clipGain.connect(trackGain)

  applyFades({
    clipGain,
    startAtSec: 0,
    playDuration: duration,
    fadeInPadding: clip.fadeIn,
    fadeOutPadding: clip.fadeOut,
  })

  source.start(0, clip.startOffset, duration)

  return await offlineContext.startRendering()
}
